import { useInfiniteQuery } from '@tanstack/react-query';

import { TableFilterState } from '@/components/data-table/filters-v2';
import { DEFAULT_PAGE_SIZE, getNextPageParam } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

import { getTimeOffRequests } from '../api/time-offs.api';
import { getTimeOffRequestsQueryKey } from '../constants/time-offs-query-keys';
import { buildTimeOffRequestsFilter } from '../mappers/time-off-filters.mapper';
import { TimeOffRequest } from '../types/time-off-request';

// Cache the list so navigating back doesn't refetch; mutations invalidate it.
const TIME_OFF_REQUESTS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

export const useTimeOffRequests = ({
  filters,
  currentUserId,
  canViewOtherTimeOffs,
}: {
  filters: TableFilterState;
  currentUserId: number | undefined;
  canViewOtherTimeOffs: boolean;
}) => {
  return useInfiniteQuery({
    queryKey: getTimeOffRequestsQueryKey(filters, currentUserId, canViewOtherTimeOffs),
    queryFn: ({ pageParam }) =>
      getTimeOffRequests({
        filter: buildTimeOffRequestsFilter({
          filters,
          currentUserId: currentUserId as number,
          canViewOtherTimeOffs,
        }),
        pagination: { cursor: pageParam, pageSize: DEFAULT_PAGE_SIZE },
      }),
    initialPageParam: null as number | null,
    enabled: currentUserId != null,
    staleTime: TIME_OFF_REQUESTS_STALE_TIME,
    getNextPageParam,
    select: data =>
      new PaginatedResponse<TimeOffRequest>({
        items: data.pages.flatMap(page => page.items),
        pagination: data.pages[data.pages.length - 1]?.pagination ?? { total: 0, cursor: null },
      }),
    // TODO(OPS-1394): drop this `retry: false` when the real backend lands.
    //   Keeps the mock random-failure path observable instead of being masked by
    //   TanStack Query's default 3 retries.
    retry: false,
  });
};
